import { useCallback, useMemo } from "react";

import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { produce } from "immer";

import {
  useCancellingSessions,
  useIsCancellingSession,
} from "src/api/agents/cancellation";
import { historyKeys } from "src/api/agents/history";
import { agentExecutionApi, copilotExecutionApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type {
  AgentHistoryResponse,
  HumanMessage,
  LiveStatusDetail,
  SessionMessagesResponseMessagesInner,
  SessionResponse,
  SessionResultResponse,
  TerminationReason,
  TimekeepingCategory,
  TimekeepingCategorySummary,
} from "src/clients/agent-management";
import { SessionStatus } from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { errorMessage } from "src/utils/stringUtils";

export type RuntimeByCategory = Partial<
  Record<TimekeepingCategory, TimekeepingCategorySummary>
>;

// ---------------------------------------------------------------------------
// Playground session state types (shared across agents, agentNode, copilot)
// ---------------------------------------------------------------------------

export type AgentPlaygroundFormValues = {
  structuredInput: Record<string, unknown>;
  instructions: string;
};

/**
 * Substatuses for active session state.
 * - booting: Container warming up (ping in-flight, playground only)
 * - pending: Backend pending state (kept distinct from running for surfaces that want to display it)
 * - running: Agent actively executing
 * - paused: Async pause
 * - cancelling: Optimistic frontend overlay — user clicked Cancel, backend hasn't acked yet
 */
export type ActiveSubstatus =
  | "booting"
  | "pending"
  | "running"
  | "paused"
  | "cancelling";

/**
 * Substatuses for stopped session state.
 * - cancelled: User clicked stop
 * - failed: Session failed with termination info
 * - success: Session completed successfully with result
 */
export type StoppedSubstatus = "cancelled" | "failed" | "success";

/**
 * Canonical frontend-derived session state — composite of {status, substatus}.
 * Use this anywhere a UI component needs to display or branch on session state.
 *
 * `PlaygroundSessionState` is `AgentSessionState` plus the playground extras;
 * the two share the same backend→composite derivation via `deriveAgentSessionState`.
 */
export type AgentSessionState =
  | { status: "active"; substatus: ActiveSubstatus }
  | { status: "stopped"; substatus: StoppedSubstatus };

/**
 * Base fields shared by active and stopped session states.
 */
type SessionStateBase = {
  sessionId: string;
  sessionMessages: SessionMessagesResponseMessagesInner[];
  inputSchema: Record<string, unknown> | null;
  systemPrompt: string | null;
  submittedInput: AgentPlaygroundFormValues;
  liveStatusDetail: LiveStatusDetail | null;
  runtimeByCategory: RuntimeByCategory;
};

/**
 * Discriminated union for playground session state.
 * Uses status/substatus hierarchy for clean type narrowing.
 */
export type PlaygroundSessionState =
  | { status: "idle" }
  | (SessionStateBase & {
      status: "active";
      substatus: ActiveSubstatus;
    })
  | (SessionStateBase & {
      status: "stopped";
      substatus: "success";
      result: SessionResultResponse;
    })
  | (SessionStateBase & {
      status: "stopped";
      substatus: "failed";
      terminationReason: TerminationReason;
      terminationMessage: string | null;
    })
  | (SessionStateBase & {
      status: "stopped";
      substatus: "cancelled";
    });

const syncSessionToHistoryCache = (
  queryClient: QueryClient,
  wsId: string,
  session: SessionResponse,
) => {
  queryClient.setQueriesData<InfiniteData<AgentHistoryResponse>>(
    { queryKey: historyKeys.sessions(wsId) },
    (oldData) => {
      if (!oldData) return oldData;

      return produce(oldData, (draft) => {
        draft.pages.forEach((page) => {
          page.sessions.forEach((s, i) => {
            if (s.session_id === session.session_id) {
              page.sessions[i] = session;
            }
          });
        });
      });
    },
  );
};

const PREFIX_CACHE_KEY = "agent-execution";

const executionKeys = {
  all: (wsId: string) => [PREFIX_CACHE_KEY, wsId] as const,
  session: (wsId: string, sessionId: string) =>
    [...executionKeys.all(wsId), "sessions", sessionId] as const,
};

export const useAgentExecutionClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  const client = useMemo(
    () =>
      workspace?.base_url ? agentExecutionApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );

  return { client, workspace };
};

export const useCopilotExecutionClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  const client = useMemo(
    () =>
      workspace?.base_url ? copilotExecutionApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );

  return { client, workspace };
};

type UseAgentSessionOptions = {
  /** Whether to poll for updates. Defaults to true. Set to false to disable polling. */
  polling: boolean;
};

/**
 * Pure backend → frontend composite mapping. Used by both `useAgentSessionState`
 * and `useSessionToPlaygroundState` so the conversion lives in one place.
 *
 * The optimistic `cancelling` overlay wins over any active backend status.
 * Terminal backend statuses (success/failed/cancelled) are surfaced as-is —
 * the cancelling flag does not override a terminal state.
 *
 * `booting` is not emitted here: it's a playground-only state derived from
 * client-side ping in-flight, layered on top by the playground context.
 */
const deriveAgentSessionState = (
  backendStatus: SessionStatus,
  isCancelling: boolean,
): AgentSessionState => {
  if (backendStatus === SessionStatus.SUCCESS) {
    return { status: "stopped", substatus: "success" };
  }
  if (backendStatus === SessionStatus.FAILED) {
    return { status: "stopped", substatus: "failed" };
  }
  if (backendStatus === SessionStatus.CANCELLED) {
    return { status: "stopped", substatus: "cancelled" };
  }
  if (isCancelling) {
    return { status: "active", substatus: "cancelling" };
  }
  if (backendStatus === SessionStatus.PAUSED) {
    return { status: "active", substatus: "paused" };
  }
  if (backendStatus === SessionStatus.PENDING) {
    return { status: "active", substatus: "pending" };
  }
  return { status: "active", substatus: "running" };
};

export const useAgentSession = (
  sessionId: string | null,
  options: UseAgentSessionOptions,
) => {
  const { polling } = options;
  const { client, workspace } = useAgentExecutionClient();
  const wsId = workspace?.id ?? "";
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: executionKeys.session(wsId, sessionId ?? ""),
    queryFn: async (): Promise<SessionResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      const response =
        await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
          sessionId,
          includeMessages: true,
        });

      syncSessionToHistoryCache(queryClient, wsId, response.data);

      return response.data;
    },
    enabled: !!client && !!sessionId,
    refetchInterval: (query) => {
      if (!polling) {
        return false;
      }
      // Stop polling on error
      if (query.state.status === "error") {
        return false;
      }
      // Stop polling when session is complete
      const data = query.state.data;
      if (
        data?.status === "success" ||
        data?.status === "failed" ||
        data?.status === "cancelled"
      ) {
        return false;
      }
      // Poll every 1 second while running
      return 1000;
    },
    throwOnError: false,
  });
};

/**
 * Cancels an in-flight agent session via the backend cancel endpoint.
 *
 * The endpoint is synchronous and may block several seconds while the backend
 * cleans up in-flight flow tools. Callers are expected to flip a frontend-only
 * "cancelling" flag before firing the mutation so the UI feels responsive
 * (see `CancellingSessionsContext`). On success we invalidate the session and
 * history caches; polling will report the terminal `cancelled` state.
 */
export const useCancelAgentSession = () => {
  const { client, workspace } = useAgentExecutionClient();
  const wsId = workspace?.id ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      await client.cancelSessionAgentsManagementApiV0SessionsSessionIdCancelPost(
        { sessionId },
      );
      return sessionId;
    },
    onSuccess: (sessionId) => {
      queryClient.invalidateQueries({
        queryKey: executionKeys.session(wsId, sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: historyKeys.sessions(wsId),
      });
    },
  });
};

export const getSessionSystemPrompt = (
  session: SessionResponse | undefined,
): string | null => {
  const systemPrompt =
    session?.agent_version_config?.model_config?.system_prompt;
  return systemPrompt?.trim() ? systemPrompt : null;
};

export const useSessionToPlaygroundState = (
  session: SessionResponse | undefined,
  sessionId: string | null,
): PlaygroundSessionState | null => {
  const isCancelling = useIsCancellingSession(sessionId);

  return useMemo((): PlaygroundSessionState | null => {
    if (!session || !sessionId) return null;

    const sessionMessages = session.messages ?? [];
    const inputSchema =
      (session.agent_version_config?.input_schema as
        | Record<string, unknown>
        | undefined) ?? null;
    const firstHumanMessage = sessionMessages.find((m) => m.type === "human") as
      | HumanMessage
      | undefined;
    const baseState: SessionStateBase = {
      sessionId,
      sessionMessages,
      inputSchema,
      systemPrompt: getSessionSystemPrompt(session),
      submittedInput: {
        structuredInput: firstHumanMessage?.structured_input ?? {},
        instructions: firstHumanMessage?.invocation_message ?? "",
      },
      liveStatusDetail: session.live_status_detail ?? null,
      runtimeByCategory:
        (session.metrics?.execution?.runtime_by_category as
          | RuntimeByCategory
          | undefined
          | null) ?? {},
    };

    const sessionState = deriveAgentSessionState(session.status, isCancelling);

    if (sessionState.status === "stopped") {
      if (sessionState.substatus === "success") {
        // Edge case: backend reports success but result is missing. Fall
        // through to "running" so the UI keeps polling rather than showing
        // an empty success card.
        if (!session.result) {
          return { ...baseState, status: "active", substatus: "running" };
        }
        return {
          ...baseState,
          status: "stopped",
          substatus: "success",
          result: session.result,
        };
      }
      if (sessionState.substatus === "failed") {
        return {
          ...baseState,
          status: "stopped",
          substatus: "failed",
          terminationReason: session.termination_reason ?? "error",
          terminationMessage: session.termination_message ?? null,
        };
      }
      return { ...baseState, status: "stopped", substatus: "cancelled" };
    }

    // Collapse "pending" → "running" for the playground's state
    // — the playground UX doesn't distinguish them.
    const substatus =
      sessionState.substatus === "pending" ? "running" : sessionState.substatus;
    return { ...baseState, status: "active", substatus };
  }, [session, sessionId, isCancelling]);
};

/**
 * Maps a raw backend `SessionStatus` to the canonical `AgentSessionState`
 * frontend status, applying the cancelling overlay from `CancellingSessionsContext`.
 *
 * Unlike `useSessionToPlaygroundState`, this preserves `pending` as a distinct
 * substatus; callers that want it rendered identically to `running` can merge
 * them at the display layer.
 */
export const useAgentSessionState = (
  sessionId: string | null,
  backendStatus: SessionStatus | undefined,
): AgentSessionState | undefined => {
  const isCancelling = useIsCancellingSession(sessionId);
  if (backendStatus === undefined) return undefined;
  return deriveAgentSessionState(backendStatus, isCancelling);
};

/**
 * Cancellation orchestrator: marks the session as cancelling (optimistic),
 * fires the mutation, and rolls back the optimistic flag with a failure toast
 * on error. Single source of truth for the cancel-click behaviour, used by
 * `AgentSessionActionsMenu` and `PlaygroundContext.stopSession`.
 */
export const useCancelSession = () => {
  const { markCancelling, unmarkCancelling } = useCancellingSessions();
  const { mutate: cancelMutation } = useCancelAgentSession();

  return useCallback(
    (sessionId: string) => {
      markCancelling(sessionId);
      cancelMutation(sessionId, {
        onError: (error) => {
          unmarkCancelling(sessionId);
          toastActions.failure({
            title: "Failed to cancel the run",
            description: errorMessage(error),
          });
        },
      });
    },
    [markCancelling, unmarkCancelling, cancelMutation],
  );
};
